import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type TeamColor =
  | 'blue'
  | 'indigo'
  | 'green'
  | 'amber'
  | 'red'
  | 'purple'
  | 'cyan'
  | 'emerald'

export type TeamMemberType = 'web' | 'phone'

export interface TeamMember {
  id: number
  name: string
  type: TeamMemberType
  status: string | null
  email: string | null
  phone?: string | null
}

export interface TeamGroupSummary {
  id: number
  name: string
  description: string | null
  color: TeamColor
  color_hex: string
  member_count: number
  created_at: string
}

export interface TeamGroupDetail extends TeamGroupSummary {
  members: TeamMember[]
}

export interface AvailableWebAgent {
  id: number
  name: string
  email: string
  role: 'AGENT' | 'MANAGER'
  status: string | null
  teams: number[]
}

export interface AvailablePhoneAgent {
  id: number
  name: string
  phone: string
  teams: number[]
}

export interface AvailableAgentsResponse {
  web_agents: AvailableWebAgent[]
  phone_agents: AvailablePhoneAgent[]
}

export interface CreateTeamInput {
  name: string
  description?: string | null
  color: TeamColor
}

export type UpdateTeamInput = Partial<CreateTeamInput>

export interface AddRemoveMemberInput {
  agent_id: number
  type: TeamMemberType
}

// =============================================================================
// Queries
// =============================================================================

/** GET /api/teams/ — liste sommaire (sans members). */
export function useTeamGroups() {
  return useQuery({
    queryKey: ['team-groups'],
    queryFn: async () => {
      const { data } = await api.get<TeamGroupSummary[]>('/api/teams/')
      return data
    },
    staleTime: 30_000,
  })
}

/** GET /api/teams/{id}/ — détail complet avec members. */
export function useTeamGroupDetail(teamId: number | null) {
  return useQuery({
    queryKey: ['team-group', teamId],
    queryFn: async () => {
      const { data } = await api.get<TeamGroupDetail>(`/api/teams/${teamId}/`)
      return data
    },
    enabled: !!teamId,
  })
}

/** GET /api/teams/available-agents/ — agents web + phone assignables. */
export function useAvailableTeamAgents(enabled = true) {
  return useQuery({
    queryKey: ['team-available-agents'],
    queryFn: async () => {
      const { data } = await api.get<AvailableAgentsResponse>(
        '/api/teams/available-agents/',
      )
      return data
    },
    enabled,
    staleTime: 15_000,
  })
}

// =============================================================================
// Mutations
// =============================================================================

/** POST /api/teams/ — crée une nouvelle team. */
export function useCreateTeamGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateTeamInput) => {
      const { data } = await api.post<TeamGroupSummary>('/api/teams/', input)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-groups'] })
    },
  })
}

/** PUT /api/teams/{id}/ — met à jour une team. */
export function useUpdateTeamGroup(teamId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpdateTeamInput) => {
      const { data } = await api.put<TeamGroupSummary>(
        `/api/teams/${teamId}/`,
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-groups'] })
      queryClient.invalidateQueries({ queryKey: ['team-group', teamId] })
    },
  })
}

/** DELETE /api/teams/{id}/ — supprime une team. */
export function useDeleteTeamGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (teamId: number) => {
      await api.delete(`/api/teams/${teamId}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-groups'] })
      queryClient.invalidateQueries({ queryKey: ['team-available-agents'] })
    },
  })
}

/** POST /api/teams/{id}/add-member/ — ajoute un membre à une team. */
export function useAddTeamMember(teamId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: AddRemoveMemberInput) => {
      const { data } = await api.post(
        `/api/teams/${teamId}/add-member/`,
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-group', teamId] })
      queryClient.invalidateQueries({ queryKey: ['team-groups'] })
      queryClient.invalidateQueries({ queryKey: ['team-available-agents'] })
    },
  })
}

/** POST /api/teams/{id}/remove-member/ — retire un membre. */
export function useRemoveTeamMember(teamId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: AddRemoveMemberInput) => {
      const { data } = await api.post(
        `/api/teams/${teamId}/remove-member/`,
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-group', teamId] })
      queryClient.invalidateQueries({ queryKey: ['team-groups'] })
      queryClient.invalidateQueries({ queryKey: ['team-available-agents'] })
    },
  })
}
